/**
 * OWNED BY C5. Pure routing/caching policy for the service worker.
 *
 * This module deliberately contains no DOM, no `self`, and no worker globals so that
 * every routing decision is unit-testable in the normal client test environment
 * (`vitest.client.config.ts`) and so `sw.ts` stays a thin shell around it.
 *
 * Policy (PRD F-701, N-001, N-008, §10.2):
 *   - `/api`, `/auth`, `/r`, `/receipt`   → network-only, never stored in any cache.
 *   - navigations                          → the precached app shell (`index.html`).
 *   - hashed build assets                  → precache, cache-first.
 *   - Google Fonts css + font files        → stale-while-revalidate, capped.
 *   - imagedelivery.net                    → cache-first, capped.
 *   - everything else                      → passthrough (the SW does not respond).
 */

export type RouteDecision =
  | "network-only"
  | "navigation"
  | "font-stylesheet"
  | "font-file"
  | "image-cdn"
  | "precache"
  | "passthrough";

/**
 * Path prefixes that carry account data or set cookies. The service worker must never
 * store their responses (PRD §10.2) and must never answer them from the shell.
 * These mirror `run_worker_first` in wrangler.jsonc.
 */
export const NEVER_CACHE_PREFIXES = ["/api", "/auth", "/r", "/receipt"] as const;

export const FONT_STYLESHEET_HOSTS = ["fonts.googleapis.com"] as const;
export const FONT_FILE_HOSTS = ["fonts.gstatic.com"] as const;
export const IMAGE_CDN_HOSTS = ["imagedelivery.net"] as const;

/** Cache names. The precache name gets the manifest hash appended at runtime. */
export const PRECACHE_PREFIX = "wc-precache-";
export const FONT_CACHE = "wc-fonts-v1";
export const IMAGE_CACHE = "wc-images-v1";

const DAY_MS = 86_400_000;

export const CACHE_LIMITS = {
  fonts: { maxEntries: 40, maxAgeMs: 365 * DAY_MS },
  images: { maxEntries: 120, maxAgeMs: 30 * DAY_MS },
} as const;

/** The shell served for every navigation. */
export const SHELL_PATH = "/index.html";

/** Message the page posts to a waiting worker to apply an update (see `register.ts`). */
export const SKIP_WAITING_MESSAGE = "wc:skip-waiting";
/** Message a page posts to ask the active worker for its version/cache name. */
export const VERSION_MESSAGE = "wc:version";

export interface RequestFacts {
  /** Absolute URL of the request. */
  url: string;
  method?: string;
  /** `Request.mode` — "navigate" for top-level navigations. */
  mode?: string;
  /** `Request.destination` — "document", "image", "font", "style", … */
  destination?: string;
  /** `Request.headers.get("accept")`, used as a fallback signal for navigations. */
  accept?: string | null;
}

export interface RouteContext {
  /** Origin the service worker is registered on, e.g. "https://app.example". */
  origin: string;
  /** Returns true when the absolute URL is part of the precache manifest. */
  isPrecached?: (absoluteUrl: string) => boolean;
}

/** `/api` matches `/api` and `/api/x` but not `/apiary`. */
export function hasPathPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isNeverCachePath(pathname: string): boolean {
  return NEVER_CACHE_PREFIXES.some((p) => hasPathPrefix(pathname, p));
}

function hostMatches(host: string, list: readonly string[]): boolean {
  const lower = host.toLowerCase();
  return list.some((h) => lower === h || lower.endsWith(`.${h}`));
}

export function isFontStylesheetHost(host: string): boolean {
  return hostMatches(host, FONT_STYLESHEET_HOSTS);
}

export function isFontFileHost(host: string): boolean {
  return hostMatches(host, FONT_FILE_HOSTS);
}

export function isImageCdnHost(host: string): boolean {
  return hostMatches(host, IMAGE_CDN_HOSTS);
}

function parse(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

function looksLikeNavigation(facts: RequestFacts): boolean {
  if (facts.mode === "navigate") return true;
  if (facts.destination === "document") return true;
  // Some browsers (older iOS Safari) do not expose `mode` on the request object.
  if (!facts.mode && !facts.destination && facts.accept) {
    return facts.accept.includes("text/html");
  }
  return false;
}

/**
 * The single routing decision function. Pure: same inputs → same output.
 */
export function classifyRequest(facts: RequestFacts, ctx: RouteContext): RouteDecision {
  const method = (facts.method ?? "GET").toUpperCase();
  const parsed = parse(facts.url);
  if (!parsed) return "passthrough";

  // Only http(s) GETs are ever cacheable. POST/PUT/DELETE, ws:, chrome-extension:,
  // blob: and data: all fall straight through to the network.
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "passthrough";
  if (method !== "GET") return "passthrough";

  const sameOrigin = parsed.origin === ctx.origin;

  // Account data first: this wins over every other rule, including navigations.
  if (sameOrigin && isNeverCachePath(parsed.pathname)) return "network-only";

  if (looksLikeNavigation(facts)) return sameOrigin ? "navigation" : "passthrough";

  if (isFontStylesheetHost(parsed.host)) return "font-stylesheet";
  if (isFontFileHost(parsed.host)) return "font-file";
  if (isImageCdnHost(parsed.host)) return "image-cdn";

  if (sameOrigin && ctx.isPrecached?.(parsed.href)) return "precache";

  return "passthrough";
}

/** True when the service worker should call `event.respondWith()` for this decision. */
export function handlesRequest(decision: RouteDecision): boolean {
  return decision !== "passthrough" && decision !== "network-only";
}

/** True when a response for this decision may be written to a cache. */
export function mayCacheResponse(decision: RouteDecision): boolean {
  return decision === "precache" || decision === "font-stylesheet" || decision === "font-file" || decision === "image-cdn";
}

// ---------------------------------------------------------------------------
// Precache manifest helpers (input comes from `self.__WB_MANIFEST`)
// ---------------------------------------------------------------------------

export interface PrecacheEntry {
  url: string;
  revision?: string | null;
  integrity?: string;
}

const PRECACHE_SKIP = [/\.map$/i, /^sw\.js$/i, /^workbox-.*\.js$/i, /\.DS_Store$/i, /^\.assetsignore$/i];

export function shouldPrecache(entry: PrecacheEntry): boolean {
  const url = entry.url.replace(/^\//, "");
  return !PRECACHE_SKIP.some((re) => re.test(url));
}

/**
 * Absolute URL the browser will request for a manifest entry.
 */
export function precacheRequestUrl(entry: PrecacheEntry, baseUrl: string): string {
  return new URL(entry.url, baseUrl).href;
}

/**
 * Cache key for a manifest entry. Un-hashed files (`index.html`, `manifest.webmanifest`)
 * carry a revision, which we fold into the key so a new build stores a new entry
 * instead of silently reusing the old body.
 */
export function precacheCacheKey(entry: PrecacheEntry, baseUrl: string): string {
  const url = new URL(entry.url, baseUrl);
  if (entry.revision) url.searchParams.set("__wc_rev", entry.revision);
  return url.href;
}

/** Stable, order-independent hash of the manifest → the precache cache name. */
export function precacheCacheName(entries: readonly PrecacheEntry[]): string {
  const material = entries
    .map((e) => `${e.url}|${e.revision ?? ""}`)
    .sort()
    .join("\n");
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < material.length; i++) {
    const c = material.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 + c + 0x9e3779b9, 2654435761) >>> 0;
  }
  return `${PRECACHE_PREFIX}${h1.toString(36)}${h2.toString(36)}`;
}

export interface PrecachePlan {
  cacheName: string;
  /** request URL → cache key */
  keyByUrl: Map<string, string>;
  /** every cache key that belongs to this build */
  keys: Set<string>;
  /** cache key for the app shell, if the manifest contains one */
  shellKey: string | null;
}

export function buildPrecachePlan(manifest: readonly PrecacheEntry[], baseUrl: string): PrecachePlan {
  const entries = manifest.filter(shouldPrecache);
  const keyByUrl = new Map<string, string>();
  const keys = new Set<string>();
  let shellKey: string | null = null;
  for (const entry of entries) {
    const url = precacheRequestUrl(entry, baseUrl);
    const key = precacheCacheKey(entry, baseUrl);
    keyByUrl.set(url, key);
    keys.add(key);
    if (new URL(url).pathname === SHELL_PATH) shellKey = key;
  }
  return { cacheName: precacheCacheName(entries), keyByUrl, keys, shellKey };
}

/** Caches that must survive `activate`. */
export function isCurrentCache(name: string, precacheName: string): boolean {
  return name === precacheName || name === FONT_CACHE || name === IMAGE_CACHE;
}

/**
 * Cache-expiry predicate, shared by the font and image runtime caches.
 * `dateHeader` is the stored response's `date` header (absent on opaque responses,
 * in which case the entry is kept and only the entry cap applies).
 */
export function isExpired(dateHeader: string | null | undefined, maxAgeMs: number, now: number): boolean {
  if (!dateHeader) return false;
  const stored = Date.parse(dateHeader);
  if (Number.isNaN(stored)) return false;
  return now - stored > maxAgeMs;
}

/** Which cached keys to evict so that `count` entries fit in `maxEntries` (oldest first). */
export function overflowKeys<T>(keysOldestFirst: readonly T[], maxEntries: number): T[] {
  const excess = keysOldestFirst.length - maxEntries;
  return excess > 0 ? keysOldestFirst.slice(0, excess) : [];
}

/** Responses worth storing. Opaque (`type: "opaque"`, status 0) images are kept. */
export function isCacheableResponse(res: { ok?: boolean; status?: number; type?: string }): boolean {
  if (res.type === "opaque") return true;
  if (typeof res.status === "number" && res.status === 0) return false;
  if (typeof res.ok === "boolean") return res.ok;
  return res.status === 200;
}
